import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import MenuTypeSelector from '../components/MenuTypeSelector';
import { menuService, getMenuTypes, getLanguages, API_BASE_URL } from '../utils/api';
import { useSchool } from '../contexts/SchoolContext';
import { useUser } from '../contexts/UserContext';
import { getAdminUrl } from '../utils/auth';
import { useQueryClient } from '@tanstack/react-query';
import { useMenu } from '../hooks/useMenu';
// Import notes & comments components
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComment, faDownload } from '@fortawesome/free-solid-svg-icons';
import CommentsModal from '../components/modals/CommentsModal';
import html2canvas from 'html2canvas';

const MenuPreview = () => {
  const { t, i18n } = useTranslation();
  const { menuId } = useParams();
  const navigate = useNavigate();
  const { school } = useSchool();
  const { user } = useUser();
  const queryClient = useQueryClient();
  
  // Log school data when it changes
  useEffect(() => {
    console.log("[MenuPreview] School context updated:", school);
  }, [school]);
  
  // Reference to track if a specific request should be ignored
  const requestCancelRef = useRef(0);
  
  // Check if user has permission to edit menus (Editor, Admin, or Superadmin)
  const hasEditPermission = useMemo(() => {
    if (!user || !user.schools || !user.current_school) return false;
    
    // Find current school's roles
    const currentSchool = user.schools.find(s => s.id === user.current_school.id);
    if (!currentSchool || !currentSchool.roles) return false;
    
    // Check if user has Editor, Admin, or Superadmin role
    return currentSchool.roles.some(role => 
      ['Editor', 'Admin', 'Superadmin'].includes(role)
    );
  }, [user]);
  
  // Menu state
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuError, setMenuError] = useState(null);
  const [defaultLanguage, setDefaultLanguage] = useState(null);
  
  // UI state
  const [availableMenuTypes, setAvailableMenuTypes] = useState([]);
  const [selectedMenuTypeId, setSelectedMenuTypeId] = useState(null);
  const [userSelectedMenuTypeId, setUserSelectedMenuTypeId] = useState(null);
  
  // Calendar state
  const [days, setDays] = useState([]);
  const [weekDays, setWeekDays] = useState([]);
  const [courses, setCourses] = useState([]);
  const [menuTypeCourses, setMenuTypeCourses] = useState([]);
  
  // Combined month-year state with YYYY-MM format
  const [selectedMonthYear, setSelectedMonthYear] = useState('');
  
  // State for showing/hiding notes row
  const [showNotes, setShowNotes] = useState(true); 
  
  // Ref to track previous language to detect changes
  const prevLangRef = useRef(i18n.language);
  
  // Use React Query to fetch the current menu if we have an ID
  // This will automatically update when invalidations happen
  const {
    data: menuData,
    isLoading: menuLoading,
    isError: menuQueryError
  } = useMenu(
    // Only enable this query if we have a numeric (non-composite) menuId
    menuId && !menuId.includes('-') ? menuId : undefined,
    i18n.language
  );

  // Update our local state when the query data changes
  useEffect(() => {
    if (menuData) {
      setMenu(menuData);
      setLoading(false);
      
      // Also update UI state based on the returned menu
      const month = menuData.month.toString().padStart(2, '0');
      const year = menuData.year.toString();
      setSelectedMonthYear(`${year}-${month}`);
      setSelectedMenuTypeId(menuData.menu_type_id?.toString());
    }
  }, [menuData]);

  // Initialize month-year default value
  useEffect(() => {
    // Set current month/year
    const now = new Date();
    const currentMonth = now.getMonth() + 1; // Convert from 0-11 to 1-12
    const currentYear = now.getFullYear();
    
    // Default selected month-year to current month if no ID is provided
    if (!menuId) {
      const currentMonthYear = `${currentYear}-${currentMonth.toString().padStart(2, '0')}`;
      setSelectedMonthYear(currentMonthYear);
    }
  }, [menuId]);

  // Force a refresh of the cache when the component mounts to ensure we have the latest data
  useEffect(() => {
    if (menuId && !menuId.includes('-')) {
      // Refresh the cache for the specific menu
      queryClient.invalidateQueries(['menu', menuId, i18n.language]);
      
      console.log('Refreshed menu cache for preview');
    }
  }, [menuId, i18n.language, queryClient]);
  
  // Group days by week for calendar display
  useEffect(() => {
    if (menu && menu.days) {
      // Make sure menu.month is properly formatted for date calculations
      let monthDate;
      console.log(`[MonthDebug] Initial menu.month value:`, menu.month, typeof menu.month);
      
      if (typeof menu.month === 'string') {
        // If it's just a year-month string like "2025-03", append a day
        if (/^\d{4}-\d{2}$/.test(menu.month)) {
          monthDate = new Date(`${menu.month}-01T00:00:00`);
          console.log(`[MonthDebug] Parsed from YYYY-MM string: ${menu.month}-01 -> `, monthDate.toISOString());
        } else {
          monthDate = new Date(menu.month);
          console.log(`[MonthDebug] Parsed from other string format: ${menu.month} -> `, monthDate.toISOString());
        }
      } else if (typeof menu.month === 'object' && menu.month.year && menu.month.month) {
        // If it's an object with year and month properties
        monthDate = new Date(menu.month.year, menu.month.month - 1, 1); // Month is 1-indexed, convert to 0-indexed
        console.log(`[MonthDebug] Parsed from object: year=${menu.month.year}, month=${menu.month.month} -> `, monthDate.toISOString());
      } else if (typeof menu.month === 'number') {
        // If it's just a numeric month (1-12)
        const year = menu.year || new Date().getFullYear();
        monthDate = new Date(year, menu.month - 1, 1); // Month is 1-indexed, convert to 0-indexed
        console.log(`[MonthDebug] Parsed from numeric month: ${menu.month} with year ${year} -> `, monthDate.toISOString());
      } else {
        // Fallback to current date
        monthDate = new Date();
        console.log(`[MonthDebug] Using fallback current date: `, monthDate.toISOString());
      }
      
      // Get the year and month
      const year = monthDate.getFullYear();
      const month = monthDate.getMonth(); // 0-11
      console.log(`[MonthDebug] Extracted year=${year}, month=${month} (${month+1} human month)`);
      
      // Filter days to only weekdays (Monday-Friday)
      const weekdaysOnly = menu.days.filter(day => {
        // Create a proper date object for this day
        const date = new Date(year, month, day.day);
        const dayOfWeek = date.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
        const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5; // 1=Monday, 5=Friday
        
        console.log(`[FilterDebug] Day ${day.day}: ${date.toISOString()}, day of week: ${dayOfWeek} (${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dayOfWeek]}), isWeekday: ${isWeekday}`);
        
        return isWeekday;
      });
      
      console.log(`[FilterDebug] Filtered ${menu.days.length} days to ${weekdaysOnly.length} weekdays`);
      
      // Group days by week
      const weeks = [];
      let currentWeek = [];
      
      // Sort days by day number
      weekdaysOnly.sort((a, b) => a.day - b.day);
      
      console.log(`[CalendarDebug] Year: ${year}, Month: ${month} (0-based in JS where 0=Jan, 11=Dec), Total days: ${weekdaysOnly.length}`);
      
      // Process each day
      weekdaysOnly.forEach(day => {
        // Create a proper date object for this day - JS months are 0-based
        // month variable is already 0-based from monthDate.getMonth() earlier
        const date = new Date(year, month, day.day);
        const dayOfWeek = date.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
        
        console.log(`[CalendarDebug] Processing day ${day.day}: Date ${date.toISOString()}, Day of week: ${dayOfWeek} (${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dayOfWeek]})`);
        
        // Start a new week if this is Monday or it's the first day
        if (dayOfWeek === 1 || currentWeek.length === 0) {
          if (currentWeek.length > 0) {
            console.log(`[CalendarDebug] Pushing week to weeks array:`, JSON.stringify(currentWeek.map(d => d ? d.day : null)));
            weeks.push(currentWeek);
          }
          currentWeek = Array(5).fill(null); // 5 days per week (Mon-Fri)
          console.log(`[CalendarDebug] Starting new week for day ${day.day}`);
        }
        
        // Place the day in the correct position (0=Monday in our array)
        // dayOfWeek of 1 (Monday) goes to index 0, 2 goes to index 1, etc.
        if (dayOfWeek >= 1 && dayOfWeek <= 5) { // Only handle weekdays
          console.log(`[CalendarDebug] Placing day ${day.day} in position ${dayOfWeek - 1} of current week`);
          currentWeek[dayOfWeek - 1] = day;
        } else {
          console.log(`[CalendarDebug] Skipping day ${day.day} with day of week ${dayOfWeek} - not a weekday`);
        }
      });
      
      // Add the last week
      if (currentWeek.length > 0) {
        console.log(`[CalendarDebug] Pushing final week to weeks array:`, JSON.stringify(currentWeek.map(d => d ? d.day : null)));
        weeks.push(currentWeek);
      }
      
      console.log(`[CalendarDebug] Final weeks array:`, JSON.stringify(weeks.map(week => week.map(d => d ? d.day : null))));
      setDays(weeks);
      
      // Set weekday headers with capitalized first letter
      const weekdayNames = Array.from({ length: 5 }, (_, i) => {
        // Use a consistent reference date to get day names
        // This creates a date for Monday, Tuesday, Wednesday, etc.
        const date = new Date(2023, 0, 2 + i); // Jan 2, 2023 was a Monday
        const dayName = date.toLocaleDateString(i18n.language, { weekday: 'long' });
        // Log the day name and index for debugging
        console.log(`[WeekdayDebug] Index ${i} -> Day ${date.getDay()} (${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}) -> ${dayName}`);
        // Capitalize first letter of the day name
        return dayName.charAt(0).toUpperCase() + dayName.slice(1);
      });
      setWeekDays(weekdayNames);
      
      console.log(`[WeekdayDebug] Final weekday headers: ${JSON.stringify(weekdayNames)}`);
      
      // Extract unique course names from menu items
      if (weekdaysOnly.length > 0) {
        const menuCoursesSet = new Set();
        weekdaysOnly.forEach(day => {
          if (day.items && day.items.length > 0) {
            day.items.forEach(item => {
              if (item.course_name) {
                menuCoursesSet.add(item.course_name);
              }
            });
          }
        });
        
        // Convert to array and sort alphabetically
        const menuCourses = Array.from(menuCoursesSet);
        setCourses(menuCourses);
      }
    }
  }, [menu, i18n.language]);

  // Fetch menu data and menu types
  useEffect(() => {
    const fetchData = async () => {
      // Check if language changed and reset course states if it did
      if (prevLangRef.current !== i18n.language) {
        console.log(`Language changed from ${prevLangRef.current} to ${i18n.language}. Resetting course states.`);
        setCourses([]); // Reset courses extracted from menu
        setMenuTypeCourses([]); // Reset courses fetched for menu type
        prevLangRef.current = i18n.language; // Update the ref
      }

      if (!school) return;
      
      // Skip this effect if the user is actively changing the month/year
      // as the handleMonthYearChange function will handle the data fetching
      if (requestCancelRef.current > 0) {
        console.log('Skipping fetchData effect due to active user interaction');
        return;
      }
      
      // Increment the request cancel reference to cancel any in-progress requests
      requestCancelRef.current += 1;
      const currentRequestId = requestCancelRef.current;
      
      try {
        setLoading(true);
        setError(null);
        setMenuError(null);
        
        // Get the school's default language for proper fallbacks
        if (school.default_language_id) {
          // Find the language code associated with the default language ID
          try {
            const languages = await getLanguages();
            
            // Check if this request has been superseded
            if (currentRequestId !== requestCancelRef.current) {
              console.log('Aborting outdated fetchData request after fetching languages');
              return;
            }
            
            const defaultLang = languages.find(lang => lang.id === school.default_language_id);
            if (defaultLang) {
              setDefaultLanguage(defaultLang.code);
            }
          } catch (err) {
            console.error('Error fetching languages:', err);
          }
        }
        
        // Fetch available menu types for the school
        const menuTypesData = await getMenuTypes(school.id, i18n.language);
        
        // Check if this request has been superseded
        if (currentRequestId !== requestCancelRef.current) {
          console.log('Aborting outdated fetchData request after fetching menu types');
          return;
        }
        
        // Admin users see all menu types, not just visible ones
        setAvailableMenuTypes(menuTypesData);
        
        // If we have a user-selected menu type, we should respect that over any other values
        // This prevents race conditions where the user selection is overridden
        if (userSelectedMenuTypeId !== null) {
          console.log(`Using user-selected menu type ID: ${userSelectedMenuTypeId} instead of URL menu ID`);
          
          if (selectedMonthYear) {
            // Extract month and year from selectedMonthYear
            const [year, month] = selectedMonthYear.split('-');
            
            try {
              // Use the efficient check-direct endpoint instead of fetching all menus
              const monthNum = parseInt(month, 10);
              const yearNum = parseInt(year, 10);
              const menuTypeIdNum = parseInt(userSelectedMenuTypeId, 10);
              
              console.log('[MenuPreview] Using efficient check-direct approach for user selection');
              const matchingMenu = await menuService.checkExistingMenu(
                school.id, 
                monthNum, 
                yearNum, 
                menuTypeIdNum
              );
              
                // Check if this request has been superseded
                if (currentRequestId !== requestCancelRef.current) {
                  console.log('Aborting outdated fetchData request after menu matching for user selection');
                  return;
                }
                
              if (!matchingMenu) {
                console.log('No matching menu found for user selection');
                setMenuError(t('menu.noMenuForType'));
                setMenu(null);
                setLoading(false);
                return;
              }
              
              // If we've found a matching menu, update the URL to include the proper ID
              // This will trigger the useMenu hook to fetch the menu data
              if (matchingMenu.id.toString() !== menuId) {
                const adminUrl = getAdminUrl(school?.subdomain);
                navigate(`${adminUrl}/preview/${matchingMenu.id}`, { replace: true });
                return; // The useEffect hook for menuData will handle setting the menu
              }
              
              // If the menuId matches and we already have menuData from React Query,
              // we don't need to fetch it again
              let menuDetails = null;
              
              if (menuData && matchingMenu.id.toString() === menuId) {
                setMenu(menuData);
                
                // Set interface state to match the menu's details
                setSelectedMonthYear(`${year}-${month}`);
                setSelectedMenuTypeId(menuDetails.menu_type_id);
              } else {
                // Otherwise, get the full menu details using the proper numeric ID
                const menuDetails = await menuService.getMenu(matchingMenu.id, i18n.language);
                
                // Check if this request has been superseded
                if (currentRequestId !== requestCancelRef.current) {
                  console.log('Aborting outdated fetchData request after fetching menu details');
                  return;
                }
                
                setMenu(menuDetails);
                
                // Set interface state to match the menu's details
                setSelectedMonthYear(`${year}-${month}`);
                setSelectedMenuTypeId(menuDetails.menu_type_id);
              }
              
              // Fetch courses for this menu type
              if (menuData?.menu_type_id) {
                const coursesData = await menuService.getMenuTypeCourses(
                  menuData?.menu_type_id,
                  i18n.language
                );
                
                // Check if this request has been superseded
                if (currentRequestId !== requestCancelRef.current) {
                  console.log('Aborting outdated fetchData request after fetching courses');
                  return;
                }
                
                setMenuTypeCourses(coursesData);
              }
            } catch (menuErr) {
              // Check if this request has been superseded
              if (currentRequestId !== requestCancelRef.current) {
                console.log('Aborting outdated fetchData request after error for user selection');
                return;
              }
              
              console.error('Failed to fetch menu for user selection:', menuErr);
              setMenuError(t('menu.noMenuForType'));
              setMenu(null);
            }
          }
        } else if (menuId) {
          // This is the original code path for URL-based menuId
          // It runs only if no user selection is active
          // Check if menuId is in the composite format (YYYY-MM-menuTypeId)
          // Composite format like "2025-03-2" would have two dashes
          if (menuId.includes('-')) {
            try {
              console.log(`MenuID in composite format: ${menuId}`);
              const parts = menuId.split('-');
              
              // If it has exactly 2 dashes (3 parts), assume it's YYYY-MM-MenuTypeId
              if (parts.length === 3) {
                const [year, month, menuTypeId] = parts;
                
                // Use the efficient check-direct endpoint instead of fetching all menus
                const monthNum = parseInt(month, 10);
                const yearNum = parseInt(year, 10);
                const menuTypeIdNum = parseInt(menuTypeId, 10);
                
                console.log('[MenuPreview] Using efficient check-direct approach for composite ID');
                const matchingMenu = await menuService.checkExistingMenu(
                  school.id, 
                  monthNum, 
                  yearNum, 
                  menuTypeIdNum
                );
                
                if (!matchingMenu) {
                  console.log('No matching menu found via check-direct for composite ID');
                  setMenuError(t('menu.noMenuForType'));
                  setMenu(null);
                  setLoading(false);
                  return;
                }
                
                console.log(`Found matching menu with ID: ${matchingMenu.id}`);
                
                // If we've found a matching menu, update the URL to include the proper ID
                // This will trigger the useMenu hook to fetch the menu data
                if (matchingMenu.id.toString() !== menuId) {
                  const adminUrl = getAdminUrl(school?.subdomain);
                  navigate(`${adminUrl}/preview/${matchingMenu.id}`, { replace: true });
                  return; // The useEffect hook for menuData will handle setting the menu
                }
                
                                let menuDetails = null;
                
                // If the menuId matches and we already have menuData from React Query,
                // we don't need to fetch it again
                if (menuData && matchingMenu.id.toString() === menuId) {
                  setMenu(menuData);
                  
                  // Set interface state to match the menu's details
                  setSelectedMonthYear(`${year}-${month}`);
                  setSelectedMenuTypeId(menuDetails.menu_type_id);
                } else {
                  // Otherwise, get the full menu details using the proper numeric ID
                  const menuDetails = await menuService.getMenu(matchingMenu.id, i18n.language);
                  
                  // Check if this request has been superseded
                  if (currentRequestId !== requestCancelRef.current) {
                    console.log('Aborting outdated fetchData request after fetching menu details');
                    return;
                  }
                  
                  setMenu(menuDetails);
                  
                  // Set interface state to match the menu's details
                  setSelectedMonthYear(`${year}-${month}`);
                  setSelectedMenuTypeId(menuDetails.menu_type_id);
                }
                
                // Fetch courses for this menu type
                if (menuTypeId) {
                  const coursesData = await menuService.getMenuTypeCourses(
                    menuTypeId,
                    i18n.language
                  );
                  
                  // Check if this request has been superseded
                  if (currentRequestId !== requestCancelRef.current) {
                    console.log('Aborting outdated fetchData request after fetching courses');
                    return;
                  }
                  
                  setMenuTypeCourses(coursesData);
                }
              } else {
                // If it's not in the expected format, try direct lookup by numeric ID
                const menuData = await menuService.getMenu(menuId, i18n.language);
                setMenu(menuData);
                
                // Update UI state based on the returned menu
                const month = menuData.month.toString().padStart(2, '0');
                const year = menuData.year.toString();
                setSelectedMonthYear(`${year}-${month}`);
                
                // Update selected menu type based on menu data
                setSelectedMenuTypeId(menuData.menu_type_id);
                
                // Fetch courses for this menu type
                if (menuData.menu_type_id) {
                  const coursesData = await menuService.getMenuTypeCourses(
                    menuData.menu_type_id,
                    i18n.language
                  );
                  setMenuTypeCourses(coursesData);
                }
              }
            } catch (error) {
              console.error('Error processing menu ID:', error);
              setError('Failed to load menu. Invalid menu ID format.');
            }
          } else {
            // Direct lookup by numeric ID - this is handled by the useMenu hook
            // so we don't need to do anything here
            console.log('Numeric menu ID detected, letting useMenu hook handle it');
          }
        } else {
          // CURRENT MONTH: Load default menu or first available
          // This code is similar to PublicMenuViewer.js but uses the authenticated admin API
          
          // Default to current month/year
          const now = new Date();
          const month = now.getMonth() + 1;
          const year = now.getFullYear();
          setSelectedMonthYear(`${year}-${month.toString().padStart(2, '0')}`);
          
          // Use school's default menu type or first available
          let menuTypeIdToUse = school.default_menu_type_id;
          if (!menuTypeIdToUse || !menuTypesData.some(mt => mt.id === menuTypeIdToUse)) {
            menuTypeIdToUse = menuTypesData.length > 0 ? menuTypesData[0].id : null;
          }
          
          setSelectedMenuTypeId(menuTypeIdToUse);
          
          if (menuTypeIdToUse) {
            // Fetch courses for the menu type
            const coursesData = await menuService.getMenuTypeCourses(
              menuTypeIdToUse,
              i18n.language
            );
            setMenuTypeCourses(coursesData);
            
            // Try to fetch menu data for the current month and selected menu type
            try {
              console.log(`Looking up default menu for year=${year}, month=${month}, menuTypeId=${menuTypeIdToUse}`);
              
              // Use the efficient check-direct endpoint instead of fetching all menus
              console.log('[MenuPreview] Using efficient check-direct approach for default case');
              const matchingMenu = await menuService.checkExistingMenu(
                school.id, 
                month, 
                year, 
                parseInt(menuTypeIdToUse, 10)
              );
              
              if (!matchingMenu) {
                console.log('No matching default menu found via check-direct');
                setMenuError(t('menu.noMenuForType'));
                setMenu(null);
                setLoading(false);
                return;
              }
              
              console.log(`Found matching default menu with ID: ${matchingMenu.id}`);
              
              // Get the full menu details using the proper numeric ID
              const menuData = await menuService.getMenu(matchingMenu.id, i18n.language);
        setMenu(menuData);
            } catch (menuErr) {
              console.error('Failed to fetch default menu data:', menuErr);
              setMenuError(t('menu.noMenuForType'));
              setMenu(null);
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setError('Failed to load menu data. Please try again later.');
      } finally {
        // Only set loading to false if this is still the most recent request
        if (currentRequestId === requestCancelRef.current) {
          setLoading(false);
        }
      }
    };
    
    fetchData();
  }, [menuId, i18n.language, school, t, userSelectedMenuTypeId, selectedMonthYear, menuData, navigate]);
  
  // Fetch menu type courses whenever selectedMenuTypeId changes
  // This ensures courses are fetched both when accessing the page directly
  // and when changing menu types within the page
  useEffect(() => {
    const fetchMenuTypeCourses = async () => {
      if (!selectedMenuTypeId || !school) return;
      
      try {
        console.log(`[MenuPreview] Fetching courses for menu type ${selectedMenuTypeId}`);
        const coursesData = await menuService.getMenuTypeCourses(
          selectedMenuTypeId,
          i18n.language
        );
        
        console.log(`[MenuPreview] Successfully fetched ${coursesData.length} courses for menu type ${selectedMenuTypeId}`);
        setMenuTypeCourses(coursesData);
      } catch (error) {
        console.error(`[MenuPreview] Failed to fetch courses for menu type ${selectedMenuTypeId}:`, error);
        // Don't throw error, just set empty array
        setMenuTypeCourses([]);
      }
    };
    
    fetchMenuTypeCourses();
  }, [selectedMenuTypeId, i18n.language, school]);

  // Handle menu type change from dropdown
  const handleMenuTypeChange = async (eventOrValue) => {
    // Increment the request ID to cancel any previous requests
    const thisRequestId = ++requestCancelRef.current;
    
    const newMenuTypeId = typeof eventOrValue === 'object' 
      ? eventOrValue.target.value 
      : eventOrValue;
    
    console.log(`Menu type changed to: ${newMenuTypeId}`);
    setSelectedMenuTypeId(newMenuTypeId);
    
    // If this request is no longer the most recent, abort
    if (thisRequestId !== requestCancelRef.current) {
      console.log('Aborting outdated menu type change request');
      return;
    }
    
    // Store the user's selection for this school in localStorage
    if (school?.subdomain) {
      localStorage.setItem(`selectedMenuType_${school.subdomain}`, newMenuTypeId);
    }
    
    // Remember that the user has explicitly selected a menu type
    setUserSelectedMenuTypeId(newMenuTypeId);
    
    try {
      setLoading(true);
      setMenuError(null);
      
      // The courses will be fetched automatically by the useEffect when selectedMenuTypeId changes
      // No need to fetch them manually here anymore
      
      // Then get menus for the current month and year
      if (selectedMonthYear) {
        const [year, month] = selectedMonthYear.split('-');
        console.log(`Looking up menu for year=${year}, month=${month}, menuTypeId=${newMenuTypeId}`);
        
        // Use the efficient check-direct endpoint instead of fetching all menus
        const monthNum = parseInt(month, 10);
        const yearNum = parseInt(year, 10);
        const menuTypeIdNum = parseInt(newMenuTypeId, 10);
        
        console.log('[MenuPreview] Using efficient check-direct approach for menu lookup');
        const matchingMenu = await menuService.checkExistingMenu(
          school.id, 
          monthNum, 
          yearNum, 
          menuTypeIdNum
        );
        
        if (!matchingMenu) {
          console.log('[MenuPreview] No matching menu found via check-direct');
          setMenuError(t('menu.noMenuForType'));
          setMenu(null);
          setLoading(false);
          return;
        }
        
        // If we've found a matching menu, update the URL to include the proper ID
        // This will trigger the useMenu hook to fetch the menu data
        if (matchingMenu.id.toString() !== menuId) {
          const adminUrl = getAdminUrl(school?.subdomain);
          navigate(`${adminUrl}/preview/${matchingMenu.id}`, { replace: true });
          return; // The useEffect hook for menuData will handle setting the menu
        }
        
        // If the menuId matches and we already have menuData from React Query,
        // we don't need to fetch it again
        if (menuData && matchingMenu.id.toString() === menuId) {
          setMenu(menuData);
          
          // Set interface state to match the menu's details
          setSelectedMonthYear(`${year}-${month}`);
          setSelectedMenuTypeId(menuData.menu_type_id);
        } else {
          // Otherwise, get the full menu details using the proper numeric ID
          const menuDetails = await menuService.getMenu(matchingMenu.id, i18n.language);
        
          // Check if this request has been superseded
        if (thisRequestId !== requestCancelRef.current) {
            console.log('Aborting outdated fetchData request after fetching menu details');
          return;
        }
        
          setMenu(menuDetails);
          
          // Set interface state to match the menu's details
          setSelectedMonthYear(`${year}-${month}`);
          setSelectedMenuTypeId(menuDetails.menu_type_id);
        }
        
        // Courses are now fetched automatically by useEffect when selectedMenuTypeId changes
        // No need to fetch them manually here anymore
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError('Failed to load data. Please try again later.');
      // Still update the menu type state even if there's an error
      setSelectedMenuTypeId(newMenuTypeId);
    } finally {
      // Only update loading state if this is still the current request
      if (thisRequestId === requestCancelRef.current) {
        setLoading(false);
      }
    }
  };
  
  // Handle month-year change from month input
  const handleMonthYearChange = async (event) => {
    const newMonthYear = event.target.value;
    console.log(`Month/year changed to: ${newMonthYear}`);
    
    // Increment the request cancel reference to cancel any in-progress requests
    requestCancelRef.current += 1;
    const thisRequestId = requestCancelRef.current;
    
    // Use the user's selected menu type if available, otherwise fall back to the current selection
    const menuTypeToUse = userSelectedMenuTypeId || selectedMenuTypeId;
    
    if (!menuTypeToUse) {
      setMenuError(t('Please select a menu type first'));
      return;
    }
    
    try {
      setLoading(true);
      setMenuError(null);
      
      // Extract month and year from new selection
      const [year, month] = newMonthYear.split('-');
      console.log(`Looking up menu for year=${year}, month=${month}, menuTypeId=${menuTypeToUse}`);
      
      try {
        // Use the efficient check-direct endpoint instead of fetching all menus
        const monthNum = parseInt(month, 10);
        const yearNum = parseInt(year, 10);
        const menuTypeIdNum = parseInt(menuTypeToUse, 10);
        
        console.log('[MenuPreview] Using efficient check-direct approach for month/year change');
        const matchingMenu = await menuService.checkExistingMenu(
          school.id, 
          monthNum, 
          yearNum, 
          menuTypeIdNum
        );
        
        if (!matchingMenu) {
          console.log('[MenuPreview] No matching menu found via check-direct for month/year change');
          
          // If this request is no longer the most recent, abort
          if (thisRequestId !== requestCancelRef.current) {
            console.log('Aborting outdated month/year change request after menu matching');
            return;
          }
          
          // Update the state only after all processing is complete
          setSelectedMonthYear(newMonthYear);
          setMenuError(t('menu.noMenuForType'));
          setMenu(null);
          setLoading(false);
          return;
        }
        
        console.log(`Found matching menu with ID: ${matchingMenu.id}`);
        
        // Get the full menu details using the proper numeric ID
        const menuDetails = await menuService.getMenu(matchingMenu.id, i18n.language);
        
        // If this request is no longer the most recent, abort
        if (thisRequestId !== requestCancelRef.current) {
          console.log('Aborting outdated month/year change request after fetching menu details');
          return;
        }
        
        // Update the month/year state along with the menu data
        setSelectedMonthYear(newMonthYear);
        setMenu(menuDetails);
        
        // If the menuId is different, update the URL
        if (menuId !== matchingMenu.id.toString()) {
          // Update URL without triggering a new navigation
          const adminUrl = getAdminUrl(school?.subdomain);
          navigate(`${adminUrl}/preview/${matchingMenu.id}`, { replace: true });
        }
      } catch (menuErr) {
        // If this request is no longer the most recent, abort
        if (thisRequestId !== requestCancelRef.current) {
          console.log('Aborting outdated month/year change request after error');
          return;
        }
        
        console.error('Failed to fetch menu for month/year:', menuErr);
        // Still update the month/year state even if there's an error
        setSelectedMonthYear(newMonthYear);
        setMenuError(t('menu.noMenuForType'));
        setMenu(null);
      }
    } catch (err) {
      console.error('Failed to fetch menu data:', err);
      setError('Failed to load menu. Please try again later.');
      // Still update the month/year state even if there's an error
      setSelectedMonthYear(newMonthYear);
    } finally {
      // Only update loading state if this is still the current request
      if (thisRequestId === requestCancelRef.current) {
        setLoading(false);
      }
    }
  };
  
  const handleBack = () => {
    navigate('/admin');
  };
  
  // Format month name with proper capitalization
  const formatMonthName = (monthString) => {
    if (!monthString) return '';
    
    // For all languages, manually capitalize first letter of the string
    let formatted = monthString.charAt(0).toUpperCase() + monthString.slice(1);
    
    // For Spanish and Catalan, ensure "de" and "del" prepositions stay lowercase
    if (i18n.language === 'es' || i18n.language === 'ca') {
      formatted = formatted
        .replace(/\bDe\b/g, 'de')
        .replace(/\bDel\b/g, 'del');
    }
    
    return formatted;
  };
  
  // Get dish name for a specific day and course with proper fallback translations
  const getDishName = (dayData, courseName) => {
    if (!dayData) return '-';
    
    // Ensure courseName is a string
    const courseNameStr = typeof courseName === 'object' ? courseName.name : courseName;
    
    const item = dayData.items && dayData.items.find(i => i.course_name === courseNameStr);
    if (!item) return '-';
    
    // Get dish name in the current language
    if (item.dish_name) {
      return item.dish_name;
    }
    
    // If not found in current language, look for translations in proper order
    if (item.dish_translations && Object.keys(item.dish_translations).length > 0) {
      // First try the school's default language
      if (defaultLanguage && item.dish_translations[defaultLanguage]) {
        return item.dish_translations[defaultLanguage];
      }
      
      // Then try any available language
      const firstAvailableTranslation = Object.values(item.dish_translations)[0];
      if (firstAvailableTranslation) {
        return firstAvailableTranslation;
      }
    }
    
    // If no translation found, return "not translated" message
    return t('menu.dishNotTranslated');
  };
  
  // Get dish tags for a specific day and course
  const getDishTags = (dayData, courseName) => {
    if (!dayData) return [];
    
    // Ensure courseName is a string
    const courseNameStr = typeof courseName === 'object' ? courseName.name : courseName;
    
    const item = dayData.items && dayData.items.find(i => i.course_name === courseNameStr);
    if (!item || !item.dish_tags) return [];
    
    return item.dish_tags || [];
  };
  
  // Format day cell style based on availability
  const getDayCellStyle = (dayData, courseName) => {
    if (!dayData) return 'bg-gray-100 text-gray-400'; // Empty day
    
    // Ensure courseName is a string
    const courseNameStr = typeof courseName === 'object' ? courseName.name : courseName;
    
    const courseItemExists = dayData.items && dayData.items.some(item => item.course_name === courseNameStr);
    
    if (!courseItemExists) {
      return `bg-gray-100 text-gray-400`; // No course item
    }
    
    return 'bg-white text-black'; // Normal cell with dish - use black text
  };
  
  // Check if a day has any dishes assigned
  const isDayEmpty = (dayData) => {
    if (!dayData || !dayData.items || dayData.items.length === 0) {
      return true;
    }
    
    // Check if all items for this day have no dish names
    return dayData.items.every(item => !item.dish_name);
  };
  
  // Add a function to handle navigation to menu editor
  const handleCreateMenu = () => {
    // Format the URL parameters for the menu editor
    // Convert the monthYear to a format the menu editor can use
    // We need to pass: year, month, menuTypeId
    
    console.log('Creating menu with:', {
      selectedMonthYear, 
      selectedMenuTypeId
    });
    
    // Extract year and month from selectedMonthYear (format: YYYY-MM)
    let year, month;
    if (selectedMonthYear) {
      [year, month] = selectedMonthYear.split('-');
    } else {
      // If no month is selected, use current month
      const now = new Date();
      year = now.getFullYear();
      month = String(now.getMonth() + 1).padStart(2, '0');
    }
    
    // Get the admin URL with the current subdomain
    const adminUrl = getAdminUrl(school?.subdomain);
    
    // Navigate to the menu editor with query parameters
    navigate(`${adminUrl}/menu-editor?year=${year}&month=${month}&menuTypeId=${selectedMenuTypeId}`);
  };
  
  // State for copy menu modal
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  
  // Comments state variables
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
  const [selectedDayIdForModal, setSelectedDayIdForModal] = useState(null);
  
  // Helper function to get note text for a day
  function getNoteForDayFromData(dayId) {
    if (!menu || !menu.days) return '';
    
    // Find the day in the menu data
    const dayData = menu.days.find(d => d.id === dayId);
    return dayData?.note?.text || '';
  }

  // Comments modal handlers
  function handleOpenCommentsModal(dayId) {
    if (!dayId) {
      console.error("Cannot view comments for a day that hasn't been saved");
      return;
    }
    setSelectedDayIdForModal(dayId);
    setIsCommentsModalOpen(true);
  }

  function handleCloseCommentsModal() {
    setIsCommentsModalOpen(false);
    setSelectedDayIdForModal(null);
  }
  
  if (loading) {
    return (
      <>
        <Navbar isAdmin={true} />
        <div className="max-w-7xl mx-auto p-4">
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        </div>
      </>
    );
  }
  
  if (error) {
    return (
      <>
        <Navbar isAdmin={true} />
        <div className="max-w-7xl mx-auto p-4">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
          <button 
            onClick={handleBack}
            className="mt-4 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            {t('Back to Dashboard')}
          </button>
        </div>
      </>
    );
  }
  
  // Apply school branding colors from the school object, with fallbacks
  const headerBgColor = school?.primary_bg_color || '#4A90E2';
  const headerTextColor = school?.primary_text_color || '#FFFFFF';
  const accentColor = school?.secondary_bg_color || '#3498DB';
  const accentTextColor = school?.secondary_text_color || '#FFFFFF';
  
  // Format month name for display (e.g., "January 2023")
  let displayMonthName = '';
  if (selectedMonthYear) {
    try {
      const [year, month] = selectedMonthYear.split('-');
      const monthDate = new Date(parseInt(year), parseInt(month) - 1, 1);
      
      // Verify the date is valid
      if (!isNaN(monthDate.getTime())) {
        displayMonthName = formatMonthName(monthDate.toLocaleString(i18n.language, { month: 'long', year: 'numeric' }));
      } else {
        console.error('Invalid date created from selectedMonthYear', selectedMonthYear);
        displayMonthName = 'Invalid date';
      }
    } catch (err) {
      console.error('Error formatting display month name:', err);
      displayMonthName = 'Error formatting date';
    }
  } else if (menu && menu.month) {
    try {
      // Parse the menu.month with the same robust logic as before
      let menuDate;
      
      if (typeof menu.month === 'string') {
        // If it's just a year-month string like "2025-03", append a day
        if (/^\d{4}-\d{2}$/.test(menu.month)) {
          menuDate = new Date(`${menu.month}-01T00:00:00`);
        } else {
          menuDate = new Date(menu.month);
        }
      } else if (typeof menu.month === 'object' && menu.month.year && menu.month.month) {
        // If it's an object with year and month properties
        menuDate = new Date(menu.month.year, menu.month.month - 1, 1);
      } else {
        // Fallback to current date
        menuDate = new Date();
      }
      
      // Verify the date is valid
      if (!isNaN(menuDate.getTime())) {
        displayMonthName = formatMonthName(menuDate.toLocaleString(i18n.language, { month: 'long', year: 'numeric' }));
      } else {
        console.error('Invalid date parsed from menu.month', menu.month);
        displayMonthName = 'Invalid date';
      }
    } catch (err) {
      console.error('Error formatting display month name from menu.month:', err);
      displayMonthName = 'Error formatting date';
    }
  }
  
  // Combine both menu type courses and courses found in the menu
  const allCourses = [...menuTypeCourses];
  
  if (courses && courses.length > 0) {
    courses.forEach(courseName => {
      // Check if this course is not already in the menuTypeCourses list
      const existingCourse = allCourses.find(c => 
        // Handle both case when c is an object with name property and when c is directly a string
        (typeof c === 'object' && c.name === courseName) || c === courseName
      );
      if (!existingCourse) {
        // Add as a simple object to maintain consistency
        // Assign a high display_order for courses not in menuTypeCourses
        allCourses.push({ name: courseName, display_order: 999 });
      }
    });
  }

  // Sort allCourses by display_order to ensure proper ordering
  allCourses.sort((a, b) => {
    const orderA = a.display_order || 999;
    const orderB = b.display_order || 999;
    return orderA - orderB;
  });
  
  // Function to get proper image URL for tag logo
  const getTagLogoUrl = (logoPath) => {
    if (!logoPath) return '';
    
    // If the path already has a protocol, return it as is
    if (logoPath.startsWith('http')) return logoPath;
    
    // Handle UUID format (Nhost storage)
    if (logoPath.includes('-') && !logoPath.includes('/')) {
      return `https://${process.env.REACT_APP_NHOST_SUBDOMAIN || 'gtwosmwmvopoqrodakmd'}.storage.${process.env.REACT_APP_NHOST_REGION || 'eu-central-1'}.nhost.run/v1/files/${logoPath}`;
    }
    
    // For relative paths with leading slash
    if (logoPath.startsWith('/')) {
      return `${window.location.origin}${logoPath}`;
    }
    
    // For legacy path format
    // Add cache-busting timestamp parameter to prevent caching issues
    const cacheBuster = `?t=${Date.now()}`;
    return `${window.location.origin}/api/uploads/logos/${logoPath.split('/').pop()}${cacheBuster}`;
  };
  
  // Function to generate and download a day's menu as an image
  const downloadDayMenu = async (dayData, coursesArray, school, selectedMonthYear, t, i18n, getTagLogoUrl, menuData, availableMenuTypes, selectedMenuTypeId) => {
    if (!dayData) return;
    
    console.log('[MenuImage] Starting menu image generation process...');
    
    // Add production environment detection
    const isProduction = process.env.NODE_ENV === 'production' || window.location.hostname !== 'localhost';
    console.log(`[MenuImage] Environment: ${isProduction ? 'Production' : 'Development'}`);
    
    // Add the CSS fix for html2canvas vertical alignment and image display
    const style = document.createElement('style');
    document.head.appendChild(style);
    style.sheet?.insertRule('body > div:last-child img { display: inline-block !important; }');
    style.sheet?.insertRule('body > div:last-child .tag-img { display: inline-block !important; visibility: visible !important; }');
    style.sheet?.insertRule('body > div:last-child .legend-tag-img { width: 40px !important; height: 40px !important; vertical-align: middle !important; }');
    // Simple CSS rule for dish tag icons
    style.sheet?.insertRule('body > div:last-child .dish-tag-img { vertical-align: middle !important; height: 0.8em !important; width: 0.8em !important; }');
    
    // Get active courses for this day
    const activeCourses = [];
    if (coursesArray && coursesArray.length > 0) {
      coursesArray.forEach(course => {
        const courseName = typeof course === 'object' ? course.name : course;
        const item = dayData.items && dayData.items.find(i => i.course_name === courseName);
        if (item) {
          activeCourses.push({
            courseName,
            dishName: item.dish_name || '-',
            tags: item.dish_tags || []
          });
        }
      });
    }
    
    console.log(`[MenuImage] Found ${activeCourses.length} active courses for this day`);
    
    // Calculate dimensions based on number of active courses
    const maxContentHeight = 600; 
    const gridRowHeight = Math.max(90, Math.min(150, maxContentHeight / Math.max(1, activeCourses.length)));
    
    // Dynamically calculate font sizes based on the number of courses
    const courseFontSize = Math.max(28, Math.min(48, 550 / Math.max(1, activeCourses.length)));
    const dishFontSize = Math.max(36, Math.min(60, 700 / Math.max(1, activeCourses.length))); // Further increased dish font size
    
    // Create a temporary container for the image
    const container = document.createElement('div');
    
    // Use a more flexible approach with flexbox layout
    const baseWidth = 1920; // Fixed width for consistent aspect ratio
    
    // Calculate content width (total width minus padding on both sides)
    const containerPadding = 40; // More generous padding
    const contentWidth = baseWidth - (containerPadding * 2);
    
    // Set the container styles with flexible dimensions
    container.style.width = `${baseWidth}px`;
    container.style.minHeight = `${Math.round(baseWidth * (9/16))}px`; // 16:9 aspect ratio minimum
    container.style.position = 'absolute';
    container.style.top = '-9999px';
    container.style.left = '-9999px';
    container.style.padding = `${containerPadding}px`; 
    container.style.boxSizing = 'border-box';
    container.style.backgroundColor = school?.primary_bg_color || '#4A90E2';
    container.style.color = school?.primary_text_color || '#FFFFFF';
    container.style.fontFamily = 'Arial, sans-serif';
    container.style.display = 'flex';
    container.style.flexDirection = 'column';
    container.style.alignItems = 'flex-start'; // Left-align children horizontally
    container.style.justifyContent = 'flex-start'; // Stack from top
    container.style.gap = '20px'; // Add gap between flex children
    
    // Format date information
    const monthYear = selectedMonthYear.split('-');
    const year = monthYear[0];
    const month = monthYear[1];
    
    // Create a date object for proper formatting
    const dateObj = new Date(parseInt(year), parseInt(month) - 1, dayData.day);
    let formattedDate = dateObj.toLocaleDateString(i18n.language, { 
      weekday: 'long', 
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    // Capitalize the first letter of the date string
    formattedDate = formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
    
    // Create the HTML content for the image
    const header = document.createElement('div');
    header.style.display = 'flex';
    header.style.alignItems = 'center';
    header.style.justifyContent = 'flex-start'; // Left-align content
    header.style.marginBottom = '20px'; // Consistent margin
    header.style.width = '100%'; // Use full width
    header.style.flexShrink = 0; // Prevent shrinking
    
    // Add school logo if available
    if (school?.logo_url) {
      console.log(`[MenuImage] Adding school logo: ${school.logo_url}`);
      const logo = document.createElement('img');
      logo.src = school.logo_url;
      logo.style.height = '120px'; // Increased from 80px
      logo.style.objectFit = 'contain';
      logo.style.marginRight = '30px'; // Increased
      logo.crossOrigin = 'anonymous';
      logo.fetchPriority = 'high';
      logo.decoding = 'sync';
      logo.loading = 'eager';
      header.appendChild(logo);
    }
    
    // Add title section
    const titleSection = document.createElement('div');
    titleSection.style.flex = '1';
    
    // Add school name with menu type
    const schoolName = document.createElement('h1');
    // Get the selected menu type name in current language
    // More robust parsing of menu type ID to ensure proper comparison
    const safeMenuTypeId = selectedMenuTypeId ? selectedMenuTypeId.toString() : '';
    
    // Log available menu types and selected ID for debugging
    console.log(`[MenuImage] selectedMenuTypeId: ${safeMenuTypeId}, type: ${typeof safeMenuTypeId}`);
    console.log(`[MenuImage] Available menu types:`, 
      availableMenuTypes?.map(mt => `${mt.id} (${typeof mt.id}): ${mt.name}`) || 'none');
    
    // Try multiple matching approaches
    let selectedMenuType = availableMenuTypes?.find(mt => mt.id.toString() === safeMenuTypeId);
    
    // If no match with string comparison, try numeric
    if (!selectedMenuType && safeMenuTypeId) {
      const numericId = parseInt(safeMenuTypeId, 10);
      selectedMenuType = availableMenuTypes?.find(mt => mt.id === numericId);
    }
    
    // If still no match, try finding in the menu data
    if (!selectedMenuType && menuData?.menu_type_id) {
      selectedMenuType = availableMenuTypes?.find(mt => 
        mt.id === menuData.menu_type_id || 
        mt.id.toString() === menuData.menu_type_id.toString());
    }
    
    console.log(`[MenuImage] Selected menu type:`, selectedMenuType);
    const menuTypeName = selectedMenuType ? selectedMenuType.name : '';

    // Add menu type name after school name if available
    const titleText = menuTypeName 
      ? `${school?.name || ''} - ${menuTypeName}` 
      : (school?.name || '');
    
    console.log(`[MenuImage] Setting title text to: "${titleText}" (menuTypeName: "${menuTypeName}")`);
    schoolName.textContent = titleText;

    schoolName.style.margin = '0 0 15px 0'; // Increased
    schoolName.style.fontSize = '42px'; // Increased from 28px
    titleSection.appendChild(schoolName);
    
    // Add date and special name if present
    const dateTitle = document.createElement('h2');
    dateTitle.textContent = dayData.specialName ? (formattedDate + ' - ' + dayData.specialName) : formattedDate;
    dateTitle.style.margin = '0';
    dateTitle.style.fontSize = '36px'; // Increased from 24px
    titleSection.appendChild(dateTitle);
    
    header.appendChild(titleSection);
    container.appendChild(header);
    
    // Create grid container with flexible width
    const gridContainer = document.createElement('div');
    gridContainer.style.width = '100%'; // Use full container width
    gridContainer.style.boxSizing = 'border-box';
    gridContainer.style.display = 'grid';
    gridContainer.style.gridTemplateColumns = '30% 70%'; // More flexible grid
    
    // Set uniform gap of 15px both horizontally and vertically
    const gridGap = '15px';
    gridContainer.style.columnGap = gridGap;
    gridContainer.style.rowGap = gridGap; // Match horizontal gap exactly
    gridContainer.style.gap = gridGap; // Modern browsers support this shorthand
    
    gridContainer.style.marginBottom = '20px';
    gridContainer.style.flexGrow = 0; // Don't let it grow beyond content
    gridContainer.style.flexShrink = 0; // Don't let it shrink
  
    // Calculate width estimates for text sizing
    const gridGapValue = 15; // Use a different variable name to avoid redeclaration
    const totalUsableWidth = contentWidth - gridGapValue; // Account for one gap between columns
    const courseColWidth = Math.floor(totalUsableWidth * 0.3); // 30% of usable space
    const dishColWidth = totalUsableWidth - courseColWidth; // Remaining space (70% approx)
    
    // More consistent cell height calculation
    const cellHeight = 155; // Slightly reduced height for more balanced spacing appearance
    
    // Add grid items for each course and dish
    activeCourses.forEach(({ courseName, dishName, tags }) => {
      // Calculate an increased height for both cells - making it larger to accommodate the dish text
      // Use a larger height than the original gridRowHeight to reduce perceived vertical gaps
      const cellHeight = Math.max(155, gridRowHeight * 1.6); 
      
      // Course cell - using table-cell approach for better vertical alignment with html2canvas
      // Changed to white background and black text for course cells
      const courseCell = document.createElement('div');
      courseCell.style.backgroundColor = '#FFFFFF'; // White background for course cells
      courseCell.style.color = '#000000'; // Black text for course cells
      courseCell.style.borderRadius = '12px'; 
      courseCell.style.padding = '0'; // Remove padding from outer element
      courseCell.style.fontSize = `${courseFontSize}px`; 
      courseCell.style.fontWeight = 'bold';
      courseCell.style.display = 'table'; // Use table instead of flex
      courseCell.style.width = '100%';
      // Use the larger cellHeight for course cells
      courseCell.style.minHeight = `${cellHeight}px`;
      courseCell.style.height = `${cellHeight}px`; 
      courseCell.style.boxSizing = 'border-box'; // Ensure padding is included in height
      courseCell.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)'; 
      courseCell.style.overflow = 'hidden'; // Prevent text overflow
      
      // Table-cell inner div for vertical alignment
      const courseInner = document.createElement('div');
      courseInner.style.display = 'table-cell';
      courseInner.style.verticalAlign = 'middle';
      courseInner.style.textAlign = 'center';
      courseInner.style.padding = '10px 15px'; // Add padding to inner element
      courseInner.style.height = '100%'; // Ensure inner cell fills entire container
      
      const courseText = document.createElement('span');
      courseText.textContent = courseName;
      
      courseInner.appendChild(courseText);
      courseCell.appendChild(courseInner);
      
      // Dish cell - also using table-cell approach
      const dishCell = document.createElement('div');
      dishCell.style.backgroundColor = school?.secondary_bg_color || '#3498DB';
      dishCell.style.color = school?.secondary_text_color || '#FFFFFF';
      dishCell.style.borderRadius = '12px'; 
      dishCell.style.padding = '0'; // Remove padding from outer element
      dishCell.style.fontSize = `${dishFontSize}px`; 
      dishCell.style.display = 'table'; // Use table instead of flex
      dishCell.style.width = '100%';
      // Use the same larger cellHeight for dish cells
      dishCell.style.minHeight = `${cellHeight}px`;
      dishCell.style.height = `${cellHeight}px`; 
      dishCell.style.boxSizing = 'border-box'; // Ensure padding is included in height
      dishCell.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)'; 
      dishCell.style.overflow = 'hidden'; // Prevent text overflow
      
      // Table-cell inner div for vertical alignment
      const dishInner = document.createElement('div');
      dishInner.style.display = 'table-cell';
      dishInner.style.verticalAlign = 'middle';
      dishInner.style.textAlign = 'center';
      dishInner.style.padding = '10px 15px'; // Add padding to inner element
      dishInner.style.height = '100%'; // Ensure inner cell fills entire container
      dishInner.style.maxHeight = `${cellHeight}px`; // Constrain maximum height
      dishInner.style.overflow = 'hidden'; // Prevent overflow
      
      // Container for text and tags
      const dishContentGroup = document.createElement('div');
      dishContentGroup.style.display = 'inline-block'; // Use inline-block instead of flex
      dishContentGroup.style.textAlign = 'center';
      
      // Helper function to estimate text width based on font size and length
      const estimateTextWidth = (text, fontSize) => {
        // Much more conservative estimate: average character width is about 0.35x font size
        // This allows for more text to fit before shrinking is triggered
        return text.length * fontSize * 0.3;
      };

      // Get available width for dish content (accounting for padding)
      const availableDishWidth = dishColWidth - 30; // 15px padding on each side
      
      // Create text element for dish name
      const dishText = document.createElement('span');
      dishText.textContent = dishName;
      dishText.style.wordBreak = 'break-word';
      dishText.style.hyphens = 'auto';
      
      // Calculate how much space the tag icons will take
      const tagIconsCount = tags.length;
      const estimatedIconsWidth = tagIconsCount * (dishFontSize * 1.0); // 0.8em + margins, but more conservative
      
      // Estimate if text will need to wrap based on current font size
      const estimatedTextWidth = estimateTextWidth(dishName, dishFontSize);
      const totalEstimatedWidth = estimatedTextWidth + estimatedIconsWidth;
      
      // Only shrink for extremely long text with icons that truly wouldn't fit
      // Increase minimum text length threshold to 35 characters
      // And require the estimated width to exceed available width by at least 40%
      if (totalEstimatedWidth > (availableDishWidth * 1.4) && dishName.length > 35) {
        // Calculate a reduced font size (down to 70% of original, much less aggressive reduction)
        const reductionFactor = Math.max(0.8, availableDishWidth / totalEstimatedWidth);
        const reducedFontSize = Math.floor(dishFontSize * reductionFactor);
        
        console.log(`Reducing dish font size from ${dishFontSize} to ${reducedFontSize} to prevent overflow`);
        dishInner.style.fontSize = `${reducedFontSize}px`;
      }
      
      dishContentGroup.appendChild(dishText);
      
      // Add tag icons if available - inline with text like in main component
      if (school?.show_tags && tags.length > 0) {
        console.log(`[MenuImage] Adding ${tags.length} tag icons for dish: ${dishName}`);
        
        // Add a space after the dish name
        const spaceText = document.createTextNode(' ');
        dishText.appendChild(spaceText);
        
        // Display tags inline with text
        tags.forEach((tag, index) => {
          if (tag.logo_path) {
            const imgUrl = getTagLogoUrl(tag.logo_path);
            console.log(`[MenuImage] Adding tag image: ${tag.name}, URL: ${imgUrl}`);
            
            // Create the image element
            const img = document.createElement('img');
            img.src = imgUrl;
            img.alt = tag.name || '';
            img.title = tag.name || '';
            img.className = 'tag-img dish-tag-img';
            
            // Simple approach: make icons same height as capital letters
            const iconSize = '0.8em'; // Relative to font size, slightly smaller than capital letter height
            img.style.height = iconSize;
            img.style.width = iconSize;
            
            // Clear any max/min constraints
            img.style.maxHeight = 'none';
            img.style.maxWidth = 'none';
            img.style.minHeight = 'none';
            img.style.minWidth = 'none';
            img.style.marginLeft = '3px';
            img.style.marginRight = '2px';
            
            // Align with text
            img.style.verticalAlign = 'middle';
            img.style.display = 'inline-block';
            img.style.position = 'relative';
            img.style.top = '-1px'; // Slight adjustment to align with text baseline
            
            // Force loading attributes for production
            img.crossOrigin = 'anonymous';
            img.fetchPriority = 'high';
            img.decoding = 'sync';
            img.loading = 'eager';
            img.setAttribute('importance', 'high');
            
            // Enhanced logging with tag name for debugging
            img.onload = () => {
              console.log(`✅ [MenuImage] Tag image loaded: ${tag.name} (${imgUrl})`);
              // Make sure the proper size is applied
              img.style.height = iconSize;
              img.style.width = iconSize;
            };
            img.onerror = (e) => console.error(`❌ [MenuImage] Failed to load tag image: ${tag.name} (${imgUrl})`, e);
            
            // Append directly to the dish text container for inline display
            dishContentGroup.appendChild(img);
          }
        });
      }
      
      dishInner.appendChild(dishContentGroup);
      dishCell.appendChild(dishInner);
      
      // Add both cells to grid
      gridContainer.appendChild(courseCell);
      gridContainer.appendChild(dishCell);
    });
    
    container.appendChild(gridContainer);
    
    // Add tag legend if enabled
    if (school?.show_tags && menuData?.tags && menuData.tags.length > 0) {
      console.log(`[MenuImage] Adding tag legend with ${menuData.tags.length} tags`);
      
      const legendSection = document.createElement('div');
      legendSection.style.width = '100%';
      legendSection.style.flexShrink = 0;
      legendSection.style.boxSizing = 'border-box';
      legendSection.style.marginBottom = '20px';
      legendSection.style.padding = '15px';
      legendSection.style.borderRadius = '8px';
      legendSection.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
      legendSection.style.maxHeight = '220px'; // Increased for multi-line tag text
      legendSection.style.overflow = 'visible'; // Show all content without truncation
      
      const legendTitle = document.createElement('h4');
      legendTitle.textContent = t('tags.legend', 'Legend');
      legendTitle.style.margin = '0 0 15px 0'; // Increased
      legendTitle.style.fontSize = '28px'; // Increased from 18px
      legendTitle.style.textAlign = 'left'; // Ensure title stays left-aligned
      legendSection.appendChild(legendTitle);
      
      const legendGrid = document.createElement('div');
      legendGrid.style.display = 'flex';
      legendGrid.style.flexWrap = 'wrap';
      legendGrid.style.gap = '8px'; // Space between rows
      legendGrid.style.columnGap = '15px'; // More space between columns
      legendGrid.style.width = '100%';
      legendGrid.style.justifyContent = 'center'; // Center tag items horizontally
      legendGrid.style.alignItems = 'flex-start'; // Align to top for better text alignment
      legendGrid.style.maxHeight = '140px'; // Reduced max height to encourage single-line text
      legendGrid.style.overflow = 'hidden'; // Hide overflow instead of scrolling
      legendGrid.style.paddingBottom = '5px'; // Add bottom padding
      
      // Increase the legend section height to accommodate more items horizontally
      legendSection.style.maxHeight = '180px'; // Increased to give more vertical space
      legendSection.style.overflow = 'visible'; // Allow content to be visible
      
      // Helper function to estimate text width
      const estimateTextWidth = (text, fontSize) => {
        // More accurate estimation: average character width varies by language
        // For Latin characters, roughly 0.6x font size; for longer words, slightly less
        const avgCharWidth = fontSize * 0.55;
        return text.length * avgCharWidth;
      };
      
      // Calculate available width for legend items
      const availableLegendWidth = contentWidth - 30; // Account for padding
      
      menuData.tags.forEach((tag, index) => {
        if (tag.logo_path) {
          const tagItem = document.createElement('div');
          tagItem.style.display = 'flex';
          tagItem.style.alignItems = 'center'; // Center align text with logo vertically
          tagItem.style.marginRight = '12px'; // Reduced spacing to fit more items
          tagItem.style.marginBottom = '6px'; // Reduced bottom margin
          tagItem.style.minHeight = '40px'; // Minimum height
          tagItem.style.overflow = 'visible'; // Show all content
          tagItem.style.width = 'auto'; // Allow natural width
          tagItem.style.maxWidth = 'none'; // Remove width restriction to allow full text
          tagItem.style.flexShrink = '0'; // Prevent shrinking
          
          const img = document.createElement('img');
          const imgUrl = getTagLogoUrl(tag.logo_path);
          console.log(`[MenuImage] Adding legend tag image: ${tag.name}, URL: ${imgUrl}`);
          img.src = imgUrl;
          img.alt = tag.name || '';
          img.className = 'tag-img legend-tag-img'; // Add specific classes for styling
          img.style.height = '40px';
          img.style.width = '40px';
          img.style.marginRight = '6px'; // Reduced spacing between icon and text
          img.style.display = 'inline-block';
          img.style.verticalAlign = 'middle'; // Align with middle of text
          img.style.marginTop = '0'; // Remove margin top
          img.style.flexShrink = '0'; // Prevent image from shrinking
          
          // Force loading attributes for production
          img.crossOrigin = 'anonymous';
          img.fetchPriority = 'high';
          img.decoding = 'sync';
          img.loading = 'eager';
          img.setAttribute('importance', 'high');
          
          // Log loading events
          img.onload = () => console.log(`✅ [MenuImage] Legend tag image loaded: ${tag.name} (${imgUrl})`);
          img.onerror = (e) => console.error(`❌ [MenuImage] Failed to load legend tag image: ${tag.name} (${imgUrl})`, e);
          tagItem.appendChild(img);
          
          const text = document.createElement('span');
          const tagName = tag.name || '';
          
          // Calculate if this text should wrap based on estimated width
          const fontSize = 20;
          const estimatedTextWidth = estimateTextWidth(tagName, fontSize);
          const iconAndSpacingWidth = 40 + 6 + 12; // icon + margin + item spacing
          const estimatedItemWidth = estimatedTextWidth + iconAndSpacingWidth;
          
          // Estimate how many items we can fit per row
          const itemsPerRow = Math.floor(availableLegendWidth / 200); // Conservative estimate per item
          const shouldAllowWrapping = menuData.tags.length > (itemsPerRow * 2) && tagName.length > 15;
          
          console.log(`[MenuImage] Tag "${tagName}": estimated width=${Math.round(estimatedItemWidth)}px, should wrap=${shouldAllowWrapping}`);
          
          text.textContent = tagName;
          text.style.fontSize = `${fontSize}px`;
          text.style.overflow = 'visible'; // Show all text
          text.style.display = 'inline-block'; // Better display behavior
          text.style.verticalAlign = 'middle'; // Ensure text is vertically centered with the icon
          text.style.paddingTop = '1px'; // Slight adjustment for better vertical alignment
          text.style.flexShrink = '0'; // Prevent text from shrinking
          
          if (shouldAllowWrapping) {
            // Allow wrapping for very long text when we have many tags
            text.style.maxWidth = '160px'; // Constrain width to force wrapping
            text.style.whiteSpace = 'normal'; // Allow text wrapping
            text.style.lineHeight = '1.2'; // Tighter line height for wrapped text
            console.log(`[MenuImage] Allowing wrap for "${tagName}"`);
          } else {
            // Keep on single line when possible
            text.style.maxWidth = 'none'; // Remove width restriction
            text.style.whiteSpace = 'nowrap'; // Prevent text wrapping - keep on one line
            text.style.lineHeight = '1.3'; // Better line height for single line
          }
          
          tagItem.appendChild(text);
          
          legendGrid.appendChild(tagItem);
        }
      });
      
      legendSection.appendChild(legendGrid);
      container.appendChild(legendSection);
    }
    
    // Add custom content if available and enabled
    if (menuData?.custom_content && school?.include_custom_content_in_image !== false) {
      const customContentSection = document.createElement('div');
      customContentSection.style.width = '100%';
      customContentSection.style.flexShrink = 0;
      customContentSection.style.boxSizing = 'border-box';
      customContentSection.style.marginBottom = '20px';
      customContentSection.style.padding = '22px';
      customContentSection.style.borderRadius = '8px';
      customContentSection.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
      customContentSection.style.color = '#333';
      customContentSection.style.overflow = 'visible';
      customContentSection.style.display = 'flex';
      customContentSection.style.flexDirection = 'column';
      customContentSection.style.justifyContent = 'center';
      
      // Create a wrapper for the custom content
      const contentWrapper = document.createElement('div');
      // Use innerHTML for the custom content to preserve formatting
      contentWrapper.innerHTML = menuData.custom_content;
      // Increase font size for all text in custom content
      contentWrapper.style.fontSize = '20px'; // Increased from 22px
      contentWrapper.style.lineHeight = '1.4'; // Better line spacing
      contentWrapper.style.textAlign = 'left'; // Left align text
      
      // Apply styles to all paragraphs in custom content
      setTimeout(() => {
        const paragraphs = contentWrapper.querySelectorAll('p');
        if (paragraphs) {
          paragraphs.forEach(p => {
            p.style.margin = '0 0 8px 0';
            p.style.padding = '0';
            p.style.textAlign = 'left'; // Left align paragraphs
          });
        }
      }, 0);
      
      customContentSection.appendChild(contentWrapper);
      container.appendChild(customContentSection);
    }
    
    // Add a flexible spacer before the footer to push it down
    const flexSpacer = document.createElement('div');
    flexSpacer.style.width = '100%';
    flexSpacer.style.minHeight = '20px';
    flexSpacer.style.flexGrow = 1; // This will push the footer to the bottom
    container.appendChild(flexSpacer);
    
    // Add a footer with branding
    const footer = document.createElement('div');
    footer.style.width = '100%';
    footer.style.flexShrink = 0;
    footer.style.marginTop = '0';
    footer.style.marginBottom = '0';
    footer.style.textAlign = 'center';
    footer.style.fontSize = '22px';
    footer.style.padding = '10px 20px';
    footer.style.boxSizing = 'border-box';
    footer.style.overflow = 'hidden';
    footer.style.textOverflow = 'ellipsis';
    footer.style.whiteSpace = 'nowrap';
    
    // Use disclaimer text from menuData if available (for current language), fallback to school
    const disclaimerText = menuData?.disclaimer_text || 
      (typeof school?.disclaimer_text === 'string' 
        ? school.disclaimer_text 
        : school?.disclaimer_text?.[i18n.language] || school?.disclaimer_text?.en);
    
    // Only add footer if disclaimer text exists
    if (disclaimerText) {
      footer.textContent = disclaimerText;
      container.appendChild(footer);
    }
    
    // Add the container to the document body temporarily
    document.body.appendChild(container);
    
    try {
      // Enhanced image preloading with production-optimized settings
      const preloadImages = async () => {
        console.log("[MenuImage] Starting enhanced image preloading for production...");
        
        // Production-specific timeout settings
        const PRODUCTION_IMAGE_TIMEOUT = isProduction ? 10000 : 3000; // 10s for production, 3s for dev
        const MAX_RETRIES = isProduction ? 5 : 3; // More retries in production
        const RETRY_DELAY = isProduction ? 1000 : 500; // Longer delays in production
        
        console.log(`[MenuImage] Using timeout: ${PRODUCTION_IMAGE_TIMEOUT}ms, retries: ${MAX_RETRIES}, delay: ${RETRY_DELAY}ms`);
        
        // Function to convert image to base64 to avoid CORS issues
        const convertImageToBase64 = async (url) => {
          try {
            console.log(`[MenuImage] Converting to base64: ${url}`);
            const response = await fetch(url, {
              mode: 'cors',
              credentials: 'omit',
              cache: 'force-cache'
            });
            
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const blob = await response.blob();
            return new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
          } catch (error) {
            console.error(`[MenuImage] Failed to convert image to base64: ${url}`, error);
            return null;
          }
        };
        
        // Enhanced image loading with better error handling and debugging
        const ensureImageLoaded = async (img, index, maxRetries = MAX_RETRIES) => {
          const originalSrc = img.src;
          
          // If already loaded successfully, no need to do anything
          if (img.complete && img.naturalWidth !== 0) {
            console.log(`[MenuImage] Image ${index} already loaded: ${originalSrc}`);
            return true;
          }
          
          console.log(`[MenuImage] Loading image ${index}: ${originalSrc}`);
          
          // Try loading the image with retries
          for (let retries = 0; retries < maxRetries; retries++) {
            try {
              await new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                  reject(new Error(`Timeout after ${PRODUCTION_IMAGE_TIMEOUT}ms loading image: ${originalSrc}`));
                }, PRODUCTION_IMAGE_TIMEOUT);
                
                const cleanup = () => {
                  clearTimeout(timeout);
                  img.onload = null;
                  img.onerror = null;
                };
                
                img.onload = () => {
                  cleanup();
                  console.log(`✅ [MenuImage] Image ${index} loaded successfully on attempt ${retries + 1}: ${originalSrc}`);
                  resolve(true);
                };
                
                img.onerror = (e) => {
                  cleanup();
                  reject(new Error(`Failed to load image: ${originalSrc} - ${e.message || 'Unknown error'}`));
                };
                
                // For retry attempts, try different loading strategies
                if (retries > 0) {
                  console.log(`[MenuImage] Retry ${retries}/${maxRetries} for image ${index}: ${originalSrc}`);
                  
                  // Wait before retry
                  setTimeout(async () => {
                    // Try converting to base64 for better compatibility in production
                    if (retries >= 2 && isProduction) {
                      console.log(`[MenuImage] Attempting base64 conversion for retry ${retries}`);
                      const base64Url = await convertImageToBase64(originalSrc);
                      if (base64Url) {
                        img.src = base64Url;
                        return;
                      }
                    }
                    
                    // Clear and reset image source
                    img.removeAttribute('src');
                    // Force reload
                    setTimeout(() => {
                      img.crossOrigin = 'anonymous';
                      img.src = originalSrc + `?retry=${retries}&t=${Date.now()}`;
                    }, 100);
                  }, RETRY_DELAY * retries);
                } else {
                  // First attempt - just check if it's already loading
                  if (!img.src) {
                    img.src = originalSrc;
                  }
                }
              });
              
              // If we get here, the image loaded successfully
              return true;
              
            } catch (error) {
              console.warn(`⚠️ [MenuImage] Attempt ${retries + 1}/${maxRetries} failed for image ${index}: ${error.message}`);
              
              // Special logging for tag images
              const isTagImage = img.className.includes('tag-img');
              if (isTagImage) {
                console.error(`⚠️⚠️⚠️ [MenuImage] Tag image failed on attempt ${retries + 1}: ${originalSrc}`);
              }
              
              // Wait before next retry (except on last attempt)
              if (retries < maxRetries - 1) {
                await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retries + 1)));
              }
            }
          }
          
          console.error(`❌ [MenuImage] Failed to load image after ${maxRetries} attempts: ${originalSrc}`);
          return false;
        };
        
        // Get all images and preload them
        const imageElements = container.querySelectorAll('img');
        console.log(`[MenuImage] Found ${imageElements.length} images to preload`);
        
        // Log all image sources for debugging
        Array.from(imageElements).forEach((img, index) => {
          console.log(`[MenuImage] Image ${index}: src=${img.src}, class=${img.className}, complete=${img.complete}, naturalWidth=${img.naturalWidth}`);
        });
        
        // Process all images with enhanced error handling
        const loadingPromises = Array.from(imageElements).map(async (img, index) => {
          try {
            const success = await ensureImageLoaded(img, index);
            return { index, success, src: img.src };
          } catch (error) {
            console.error(`[MenuImage] Unexpected error loading image ${index}:`, error);
            return { index, success: false, src: img.src, error: error.message };
          }
        });
        
        // Wait for all images to finish loading (or failing)
        const results = await Promise.all(loadingPromises);
        
        // Log summary
        const successful = results.filter(r => r.success).length;
        const failed = results.filter(r => !r.success);
        
        console.log(`[MenuImage] Image loading summary: ${successful}/${results.length} successful`);
        
        if (failed.length > 0) {
          console.warn(`[MenuImage] Failed images:`, failed);
          
          // In production, show a warning to the user about failed images
          if (isProduction) {
            console.warn(`[MenuImage] Some images failed to load in production. This might affect the final image quality.`);
          }
        }
        
        return results;
      };
      
      // Preload all images first
      const imageResults = await preloadImages();
      
      // Add additional wait time in production to ensure all resources are ready
      if (isProduction) {
        console.log('[MenuImage] Adding extra production wait time...');
        await new Promise(resolve => setTimeout(resolve, 2000)); // Extra 2 seconds in production
      }
      
      // Calculate the natural width based on height to maintain 16:9 aspect ratio
      // First get the actual computed height of the container after all content
      const containerRect = container.getBoundingClientRect();
      const actualHeight = containerRect.height;
      
      // Calculate the proper width for 16:9 ratio
      const aspectRatio = 16/9;
      const properWidth = Math.ceil(actualHeight * aspectRatio);
      
      // Add logging to verify the calculations
      console.log(`[MenuImage] Dimensions adjustment:
        - Initial width: ${baseWidth}px
        - Actual container height: ${actualHeight}px
        - Calculated 16:9 width: ${properWidth}px
        - Aspect ratio: ${properWidth / actualHeight} (target: ${aspectRatio})
      `);
      
      // Set the container width to match the calculated width
      container.style.width = `${properWidth}px`;
      
      // Force the browser to recalculate layout before rendering
      container.getBoundingClientRect(); // This forces a reflow
      
      // If the width has changed significantly, we need to update the grid proportions
      if (Math.abs(properWidth - baseWidth) > 100) {
        console.log(`[MenuImage] Adjusting grid for new width: ${properWidth}px`);
        // Update grid to ensure it uses the new proportions correctly
        gridContainer.style.width = '100%';
        
        // Make sure all child elements of the container use the new width properly
        Array.from(container.children).forEach(child => {
          if (child.style.width && child.style.width !== '100%') {
            child.style.width = '100%';
          }
        });
      }
      
      // Use html2canvas with production-optimized settings
      console.log('[MenuImage] Starting html2canvas rendering...');
      const canvasOptions = {
        scale: 2, // High resolution
        useCORS: true, // Important for external images
        allowTaint: true,
        backgroundColor: null,
        imageTimeout: isProduction ? 15000 : 5000, // Longer timeout in production
        logging: isProduction, // Enable logging in production for debugging
        removeContainer: false, // Keep container for debugging
        foreignObjectRendering: false, // Disable for better compatibility
        width: properWidth,
        height: actualHeight,
        windowWidth: properWidth,
        windowHeight: actualHeight
      };
      
      console.log('[MenuImage] html2canvas options:', canvasOptions);
      
      const canvas = await html2canvas(container, canvasOptions);
      
      console.log(`[MenuImage] Canvas generated successfully: ${canvas.width}x${canvas.height}`);
      
      // Create a download link
      const link = document.createElement('a');
      const schoolName = school?.name?.replace(/\s+/g, '_') || 'school';
      const fileName = `${schoolName}_menu_${year}_${month}_${dayData.day}.png`;
      
      // Convert canvas to data URL and set as href
      const dataUrl = canvas.toDataURL('image/png', 1.0); // Maximum quality
      link.href = dataUrl;
      link.download = fileName;
      
      console.log(`[MenuImage] Generated image file: ${fileName}, size: ${Math.round(dataUrl.length / 1024)}KB`);
      
      // Simulate click to trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('[MenuImage] Download initiated successfully');
      
    } catch (err) {
      console.error('[MenuImage] Error generating menu image:', err);
      
      // Enhanced error reporting for production
      if (isProduction) {
        console.error('[MenuImage] Production error details:', {
          message: err.message,
          stack: err.stack,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        });
      }
      
      alert(t('menu.errorGeneratingImage', 'Error generating menu image. Please try again.') + 
            (isProduction ? ` Error: ${err.message}` : ''));
    } finally {
      // Clean up - remove the temporary container and styles
      if (container.parentNode) {
        document.body.removeChild(container);
      }
      if (style.parentNode) {
        document.head.removeChild(style);
      }
      console.log('[MenuImage] Cleanup completed');
    }
  };
  
  // Debug utility function for testing image loading in production
  // This can be called from browser console: window.debugMenuImages()
  window.debugMenuImages = async () => {
    console.log('[ImageDebug] Starting image loading test...');
    
    const isProduction = process.env.NODE_ENV === 'production' || window.location.hostname !== 'localhost';
    console.log(`[ImageDebug] Environment: ${isProduction ? 'Production' : 'Development'}`);
    
    // Test school logo
    if (school?.logo_url) {
      console.log(`[ImageDebug] Testing school logo: ${school.logo_url}`);
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        const logoResult = await new Promise((resolve) => {
          const timeout = setTimeout(() => {
            resolve({ success: false, error: 'Timeout after 10s' });
          }, 10000);
          
          img.onload = () => {
            clearTimeout(timeout);
            resolve({ success: true, dimensions: `${img.naturalWidth}x${img.naturalHeight}` });
          };
          
          img.onerror = (e) => {
            clearTimeout(timeout);
            resolve({ success: false, error: e.message || 'Load error' });
          };
          
          img.src = school.logo_url;
        });
        
        console.log(`[ImageDebug] School logo result:`, logoResult);
      } catch (error) {
        console.error(`[ImageDebug] School logo error:`, error);
      }
    }
    
    // Test tag images
    if (menu?.tags && menu.tags.length > 0) {
      console.log(`[ImageDebug] Testing ${menu.tags.length} tag images...`);
      
      for (let i = 0; i < menu.tags.length; i++) {
        const tag = menu.tags[i];
        if (tag.logo_path) {
          const imgUrl = getTagLogoUrl(tag.logo_path);
          console.log(`[ImageDebug] Testing tag ${i + 1}/${menu.tags.length}: ${tag.name} - ${imgUrl}`);
          
          try {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            
            const tagResult = await new Promise((resolve) => {
              const timeout = setTimeout(() => {
                resolve({ success: false, error: 'Timeout after 10s' });
              }, 10000);
              
              img.onload = () => {
                clearTimeout(timeout);
                resolve({ success: true, dimensions: `${img.naturalWidth}x${img.naturalHeight}` });
              };
              
              img.onerror = (e) => {
                clearTimeout(timeout);
                resolve({ success: false, error: e.message || 'Load error' });
              };
              
              img.src = imgUrl;
            });
            
            console.log(`[ImageDebug] Tag ${tag.name} result:`, tagResult);
            
            // Add a small delay between tests to avoid overwhelming the server
            await new Promise(resolve => setTimeout(resolve, 200));
          } catch (error) {
            console.error(`[ImageDebug] Tag ${tag.name} error:`, error);
          }
        }
      }
    }
    
    console.log('[ImageDebug] Image loading test completed');
  };
  
  return (
    <>
      <Navbar isAdmin={true} />
      <div className="min-h-screen bg-gray-100">
        <div className="p-4">
          <div className="flex justify-between items-center mx-auto mb-6">
            <div className="flex items-center gap-4">
              <h1 className="text-3xl font-bold">{t('admin.preview')}</h1>
            </div>
            
            {/* Right-aligned selectors moved next to the title */}
            <div className="flex items-end gap-4"> {/* Use items-end to align components at the bottom edge */}
              {/* Show Notes Toggle Switch */}
              <div>
                <label 
                  htmlFor="show-notes-toggle"
                  className="block text-sm font-medium mb-1"
                >
                  {t('menu.showNotes', 'Show Notes')}
                </label>
                <button
                  type="button"
                  id="show-notes-toggle"
                  onClick={() => setShowNotes(!showNotes)}
                  disabled={loading}
                  className={`relative inline-flex items-center my-2 h-6 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${showNotes ? 'bg-blue-600' : 'bg-gray-200'} ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  role="switch"
                  aria-checked={showNotes}
                >
                  <span
                    aria-hidden="true"
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${showNotes ? 'translate-x-6' : 'translate-x-1'}`}
                  />
                </button>
              </div>

              {/* Month-Year input field */}
              <div>
                <label className="block text-sm font-medium mb-1">{t('Month/Year')}</label>
                <input
                  type="month"
                  value={selectedMonthYear}
                  onChange={handleMonthYearChange}
                  className={`block w-48 appearance-none bg-white border border-gray-300 hover:border-gray-400 px-4 py-2 rounded shadow leading-tight focus:outline-none focus:shadow-outline ${loading ? 'opacity-60 cursor-not-allowed' : ''}`}
                  disabled={loading}
                />
              </div>
              
              {/* Menu type selector */}
              <div>
                <label className="block text-sm font-medium mb-1">{t('menu.menuType')}</label>
                <MenuTypeSelector 
                  menuTypes={availableMenuTypes} 
                  selectedMenuTypeId={selectedMenuTypeId} 
                  onChange={handleMenuTypeChange}
                  disabled={loading}
                />
              </div>
              
              {/* Add Edit button when viewing an existing menu - only for users with edit permission */}
              {menu && menu.id && hasEditPermission && (
                <div>
                  <label className="block text-sm font-medium mb-1 opacity-0">&#8203;</label>
                  <button 
                    onClick={() => {
                      const adminUrl = getAdminUrl(school?.subdomain);
                      navigate(`${adminUrl}/menu-editor/${menu.id}`);
                    }}
                    className="px-4 py-2 rounded font-medium transition-colors"
                    style={{ 
                      backgroundColor: accentColor || '#4A90E2', 
                      color: accentTextColor || '#FFFFFF'
                    }}
                    disabled={loading}
                  >
                    {t('menu.editThisMenu')}
                  </button>
                </div>
              )}
              
              {/* Loading indicator */}
              {loading && (
                <div className="flex items-center ml-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-500 mr-2"></div>
                  <span className="text-sm text-gray-600">{t('common.loading')}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Menu display area */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden relative">
            {/* Loading overlay - shown when loading */}
            {loading && (
              <div className="absolute inset-0 bg-white bg-opacity-70 z-10 flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
                <p className="text-gray-700 font-medium">{t('menu.loadingMenu')}</p>
              </div>
            )}
            
            {/* Month/year header bar */}
            <div 
              className="overflow-hidden border-t flex items-center"
              style={{ 
                borderColor: headerBgColor
              }}
            >
              {/* Month/year header with primary colors */}
              <div className="flex items-center">
                <div 
                  className="px-4 py-3 h-14 flex items-center"
                  style={{ 
                    backgroundColor: headerBgColor, 
                    color: headerTextColor 
                  }}
                >
                  <h2 className="text-3xl">
                    {displayMonthName}
                  </h2>
                </div>
                <div 
                  className="px-4 py-3 -skew-x-12 -ml-2 h-14" 
                  style={{ 
                    backgroundColor: headerBgColor
                  }}
                >
                  {/* Empty div with fixed height for skew effect */}
                </div>
              </div>
            </div>
            
            {/* Show menu error if exists, otherwise display the calendar */}
            {menuError ? (
              <div className="flex-grow flex flex-col items-center justify-center p-4 my-8">
                <div className="bg-white rounded-lg shadow-lg max-w-md w-full text-center">
                  <h2 className="text-xl font-semibold rounded-t-lg mb-4" style={{ color: headerTextColor, backgroundColor: headerBgColor, padding: '0.5rem'}}>
                    {t('menu.noMenu')}
                  </h2>
                  <p className="mb-6 mx-2 text-gray-800">
                    {menuError}
                  </p>
                  
                  {/* Add Create Menu Button - only for users with edit permission */}
                  {hasEditPermission && (
                    <button 
                      onClick={handleCreateMenu}
                      className="px-4 py-2 mb-4 rounded font-medium transition-colors"
                      style={{ 
                        backgroundColor: accentColor || '#4A90E2', 
                        color: accentTextColor || '#FFFFFF'
                      }}
                    >
                      {t('menu.createThisMenu')}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              /* Only display the calendar if there's no menu error */
              <>
                {/* Weekly calendar display - now as a single table */}
                <div className="overflow-x-auto w-full p-4">
                  {menu && menu.days && menu.days.length > 0 ? (
                    <table className="min-w-full w-full" style={{ tableLayout: 'fixed' }}>
                      <colgroup>
                        {/* First column for course names - width based on content */}
                        <col style={{ width: 'auto' }} />
                        
                        {/* Equal width columns for each day of the week */}
                        {[0, 1, 2, 3, 4].map(i => (
                          <col key={`col-${i}`} style={{ width: '18%' }} />
                        ))}
                      </colgroup>
                      <tbody>
                        {days.map((week, weekIndex) => {
                          // Filter out null days (days not in the current month)
                          const daysInCurrentMonth = week.filter(day => day !== null);
                          
                          // Skip rendering this week if it has no days in the current month
                          if (daysInCurrentMonth.length === 0) {
                            return null;
                          }
                          
                          return (
                            <React.Fragment key={`week-${weekIndex}`}>
                              {/* Header row with day names and dates */}
                              <tr>
                                <td className="border-0"></td>
                                {week.map((dayData, dayIndex) => {
                                  // Skip rendering this column if the day isn't in the current month
                                  if (dayData === null) {
                                    return <td key={`day-header-${dayIndex}`} className="border-0"></td>;
                                  }
                                  
                                  const dayName = weekDays[dayIndex];
                                  const dayNumber = dayData ? dayData.day : '';
                                  const specialName = dayData && dayData.specialName;
                                  
                                  return (
                                    <td 
                                      key={`day-header-${dayIndex}`} 
                                      className="py-2 px-3 text-center font-medium border border-gray-200"
                                      style={{ 
                                        backgroundColor: isDayEmpty(dayData) ? '#555555' : accentColor, 
                                        color: isDayEmpty(dayData) ? '#FFFFFF' : accentTextColor,
                                        wordWrap: 'break-word',
                                        hyphens: 'auto'
                                      }}
                                    >
                                      {/* Use a flex container with space-between to push button to right */}
                                      <div className="flex items-center justify-between w-full">
                                        {/* Empty divs on left for space-between to work correctly */}
                                        <div className="w-5"></div>
                                        <div className="w-5"></div>
                                        
                                        {/* Center column with day name/number and special name */}
                                        <div className="flex flex-col items-center flex-grow">
                                          <div style={{ wordWrap: 'break-word', hyphens: 'auto' }}>
                                            {dayName} {dayNumber && <span>{dayNumber}</span>}
                                          </div>
                                        
                                          {specialName && (
                                            <div className="text-sm font-normal italic" style={{ wordBreak: 'break-word', wordWrap: 'break-word', hyphens: 'auto', maxWidth: '100%' }}>
                                              {specialName}
                                            </div>
                                          )}
                                        </div>
                                        
                                        {/* Action buttons on right side */}
                                        <div className="flex items-center space-x-2">
                                          {/* Download button */}
                                          {dayData && (
                                            <div className="relative">
                                              <button
                                                type="button"
                                                onClick={() => {
                                                console.log(`[Download] Selected menu type ID before download: ${selectedMenuTypeId}`);
                                                console.log(`[Download] Menu object:`, menu);
                                                downloadDayMenu(
                                                  dayData, 
                                                  allCourses, 
                                                  school, 
                                                  selectedMonthYear, 
                                                  t, 
                                                  i18n,
                                                  getTagLogoUrl,
                                                  menu,
                                                  availableMenuTypes,
                                                  selectedMenuTypeId
                                                );
                                              }}
                                                className="text-current focus:outline-none"
                                                aria-label="Download day menu"
                                                title={t('menu.downloadDay', 'Download day menu')}
                                              >
                                                <FontAwesomeIcon icon={faDownload} className="h-4 w-4" />
                                              </button>
                                            </div>
                                          )}
                                          
                                          {/* Comment button */}
                                          {dayData && dayData.id && hasEditPermission ? (
                                            <div className="relative">
                                              {/* Unread comments indicator */}
                                              {dayData.has_unread_comments && (
                                                <div className="absolute top-0.5 -right-1 w-2.5 h-2.5 bg-orange-400 border border-white rounded-full shadow-sm"></div>
                                              )}
                                              <button
                                                type="button"
                                                onClick={() => handleOpenCommentsModal(dayData.id)}
                                                className={`text-current focus:outline-none ${(dayData.comment_count === undefined || dayData.comment_count === null || dayData.comment_count === 0) ? 'opacity-50' : ''}`}
                                                aria-label="View comments"
                                              >
                                                <FontAwesomeIcon icon={faComment} className="h-4 w-4" />
                                              </button>
                                            </div>
                                          ) : (
                                            <div className="w-5"></div> /* Empty div when no button, to maintain spacing */
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>
                              
                              {/* Notes Row - Conditionally rendered */}
                              {showNotes && (
                                <tr>
                                  <td className="border-0 text-right italic text-gray-600 pr-2" style={{ whiteSpace: 'nowrap' }}>
                                    {t('menu.notes', 'Notes')}
                                  </td>
                                  {week.map((dayData, dayIndex) => {
                                    // Skip rendering this column if the day isn't in the current month
                                    if (dayData === null) {
                                      return <td key={`day-note-${dayIndex}`} className="border-0"></td>;
                                    }
                                    
                                    const noteText = dayData?.note?.text;
                                    
                                    return (
                                      <td 
                                        key={`day-note-${dayIndex}`} 
                                        className={`p-2 border border-gray-200 ${noteText ? 'bg-yellow-50 text-yellow-800 italic' : 'bg-gray-50 text-gray-400 italic text-center'}`}
                                      >
                                        <div className="text-sm" style={{ minHeight: '1.5rem', whiteSpace: 'pre-wrap' }}>
                                          {noteText || t('menu.noNotes', 'No notes')}
                                        </div>
                                      </td>
                                    );
                                  })}
                                </tr>
                              )}
                              
                              {/* Course rows */}
                              {allCourses.map((course, courseIndex) => {
                                // Extract the course name, handling both object and string formats
                                const courseName = typeof course === 'object' ? course.name : course;
                                
                                return (
                                  <tr key={`course-${courseIndex}-week-${weekIndex}`} className="text-center">
                                    <td className="border-0 text-right italic text-gray-600 pr-2">
                                      {courseName}
                                    </td>
                                    {week.map((dayData, dayIndex) => {
                                      // Skip rendering this column if the day isn't in the current month
                                      if (dayData === null) {
                                        return <td key={`day-dish-${dayIndex}-course-${courseIndex}`} className="border-0"></td>;
                                      }
                                      
                                      return (
                                        <td 
                                          key={`day-dish-${dayIndex}-course-${courseIndex}`} 
                                          className={`p-1 border border-gray-200 ${getDayCellStyle(dayData, courseName)}`}
                                          style={{
                                            wordWrap: 'break-word',
                                            hyphens: 'auto',
                                            minHeight: '2.5rem'
                                          }}
                                        >
                                          <div className="text-sm text-center" title={getDishName(dayData, courseName)}>
                                            {getDishName(dayData, courseName)}
                                            
                                            {/* Display tag icons inline if enabled for this school AND menu type */}
                                            {school?.show_tags && availableMenuTypes.find(mt => mt.id === parseInt(selectedMenuTypeId))?.show_tags !== false && getDishTags(dayData, courseName).length > 0 && (
                                              <>
                                                {' '}
                                                {getDishTags(dayData, courseName).map((tag, i) => (
                                                  <img 
                                                    key={`tag-${i}`} 
                                                    src={getTagLogoUrl(tag.logo_path)} 
                                                    alt={tag.name || `Tag ${i+1}`}
                                                    title={tag.name || `Tag ${i+1}`}
                                                    className="h-4 w-4 object-contain inline align-text-bottom mx-0.5"
                                                  />
                                                ))}
                                              </>
                                            )}
                                          </div>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })}
                              
                              {/* Nutrient Row (Conditionally Rendered) - Moved inside week loop */}
                              {school?.show_nutrients && availableMenuTypes.find(mt => mt.id === parseInt(selectedMenuTypeId))?.show_nutrients !== false && (
                                <tr>
                                  <td className="border-0 text-right italic text-gray-600 pr-2" style={{ whiteSpace: 'nowrap' }}>
                                    {t('nutrients')}
                                  </td>
                                  {/* Map over the current week's days */} 
                                  {week.map((dayData, dayIndex) => { 
                                    // If dayData is null, render an empty cell for nutrients too
                                    if (!dayData) {
                                      return <td key={`nutrient-day-${dayIndex}`} className="border-0"></td>;
                                    }

                                    const summary = dayData?.nutrient_summary;
                                    let content = '-';
                                    let cellStyle = 'text-gray-500 bg-gray-100'; // Default for unavailable

                                    if (summary?.status === 'complete') {
                                      // Updated content string to include unit
                                      content = summary.nutrients.map(n => {
                                        const unitString = n.unit ? ` ${n.unit}` : '';
                                        const valueString = n.totalValue !== null ? Math.round(n.totalValue) : '-';
                                        return `${n.name}: ${valueString}${unitString}`;
                                      }).join(', ') || t('menu.nutrientDataComplete');
                                      cellStyle = 'text-green-700 bg-gray-50';
                                    } else if (summary?.status === 'incomplete') {
                                      content = t('menu.nutrientDataIncomplete');
                                      cellStyle = 'text-yellow-700 italic bg-gray-50';
                                    }
                                    // 'unavailable' status uses the default content and style

                                    return (
                                      <td 
                                        key={`nutrient-day-${dayIndex}`} 
                                        className={`py-2 px-3 text-center text-xs border border-gray-200 ${cellStyle}`}
                                        style={{
                                          wordWrap: 'break-word',
                                          hyphens: 'auto'
                                        }}
                                        title={typeof content === 'string' ? content : ''}
                                      >
                                        {content}
                                      </td>
                                    );
                                  })}
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div className="p-8 text-center text-gray-500">
                      {t('menu.noItems')}
                    </div>
                  )}
                </div>
                
                {/* School's custom content */}
                {menu && menu.custom_content && (
                  <div 
                    className="mt-4 p-4 mx-4 mb-4 border border-gray-200 rounded bg-gray-50 text-black public-custom-content"
                    dangerouslySetInnerHTML={{ __html: menu.custom_content }}
                  />
                )}

                {/* Tag Legend - only shown when tags are enabled */}
                {school?.show_tags && availableMenuTypes.find(mt => mt.id === parseInt(selectedMenuTypeId))?.show_tags !== false && menu?.tags && menu.tags.length > 0 && (
                  <div className="p-4 bg-gray-50 border-t border-gray-300">
                    <h3 className="text-lg font-semibold mb-2">{t('tags.legend', 'Tag Legend')}</h3>
                    <div className="flex flex-wrap gap-3">
                      {menu.tags.map((tag, index) => (
                        <div key={`legend-${index}`} className="flex items-center">
                          <img 
                            src={getTagLogoUrl(tag.logo_path)}
                            alt={tag.name || `Tag ${index+1}`}
                            className="h-5 w-5 object-contain mr-1"
                          />
                          <span className="text-sm">{tag.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Footer */}
                <div 
                  className="p-4 text-center text-sm" 
                  style={{ backgroundColor: headerBgColor, color: headerTextColor }}
                >
                  {t('menu.footer')}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Notes and Comments Modals */}
      <CommentsModal
        isOpen={isCommentsModalOpen}
        onClose={handleCloseCommentsModal}
        dayId={selectedDayIdForModal}
        menuId={menu?.id}
      />
    </>
  );
};

export default MenuPreview;